import {useLocation} from 'react-router';
import {Button, Tooltip, Typography} from '@material-ui/core';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import WishConfirmModal from './WishConfirmModal';
import DataErrorFallback from '@super-template/core/src/DataErrorFallback';
import messages from '@super-template/messages';
import {useHolidays} from 'api';
import {findKey, pickBy} from 'lodash';
import {useContext, useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {generatePath, Redirect, useHistory, useParams} from 'react-router-dom';
import {useQueryParams} from 'use-query-params';
import Page from '../../layouts/Page';
import {SettingsContext} from '../application/utils';
import {filterModel, getPrimary, getWishArray, savePrimary, saveWishArray,} from '../utils';
import Filter from './Filter';
import HolidayTable from './HolidayTable';
import Instructions from './Instructions';
import AlertWithTimer from '@super-template/core/src/AlertWithTimer';
import useSessionStorage from '@postinumero/storage/useSessionStorage';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {path as application} from "../application";
import {path as welcome} from "../welcome";

export const path = '/:organization';
export const exact = true;

const useStyles = makeStyles((theme) => ({
  button: {
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(1),
  },
  none: {
    padding: theme.spacing(2),
    fontStyle: 'italic',
  },
}));

const ApplyButton = ({size, setConfirm, className}) => (
  <Button variant="contained" disabled={size < 1} color="primary" className={className}
          onClick={() => setConfirm(true)}>
    <FormattedMessage {...messages.apply_for_holiday} />
  </Button>
);

const TooltipSearch = ({isHolidayOrganization, ...props}) => (
  <Tooltip title={<FormattedMessage {...isHolidayOrganization ? messages.choose_holidays : messages.choose_courses} />}
           placement="top-start" arrow>
    <div style={{display: 'inline-block'}}><ApplyButton {...props} /></div>
  </Tooltip>
);

const getAlert = (h, l) => {
  const key = findKey(l, (v) => v === true) || false;
  return (h || !key) ? key : key.includes("add") ? "course_added" : "course_removed";
}
const getPagination = ({filter, familyHoliday, count, ...props}) => ({
  ...props,
  page: filter.page ?? 1,
  filter: pickBy(filter),
  familyHoliday,
  count: count ?? 0
})
export const component = function Calendar() {
  const {organization} = useParams();
  const query = useLocation().search;
  const classes = useStyles();
  const history = useHistory();
  const historyState = history.location.state;
  const {hideOptions: {hidePartnerColumn}, organization: {isHolidayOrganization, ...o}} = useContext(SettingsContext);
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'), {noSsr: true});
  const [familyHoliday] = useSessionStorage('familyHoliday');
  const [filter, setFilter] = useQueryParams(filterModel());
  const [pagination, setPagination] = useState(getPagination({pageSize: 20, filter, familyHoliday}));
  const holidays = useHolidays({organization, ...pagination});
  const [confirm, setConfirm] = useState(false);
  const [primary, setPrimary] = useState(getPrimary());
  const [wishChanged, setWishChanged] = useState(getAlert(isHolidayOrganization, historyState));
  const [wishArray, setWishArray] = useState(getWishArray());
  const askHolidayType = o.hasAdultHoliday && o.hasFamilyHoliday && (!familyHoliday && familyHoliday !== false) && !new URLSearchParams(query).get('theme');
  const navigate = () => history.push(generatePath(application, {organization}));
  const selectPrimary = ({target}) => setPrimary(savePrimary(wishArray.filter((item) => parseInt(target.value) === item.id)[0]));
  const buttonProps = {className: classes.button, setConfirm, navigate, size: wishArray.length};

  useEffect(() => {
    if (historyState) history.replace({...history.location, state: null});
  }, [historyState, history]);

  useEffect(() => {
    if (holidays) setPagination((p) => getPagination({...p, filter, familyHoliday, count: holidays.maxResults}));
  }, [filter, familyHoliday, holidays]);

  const removeWish = ({id}) => {
    const filtered = wishArray.filter((item) => parseInt(item.id) !== parseInt(id));
    if (parseInt(primary.id) === parseInt(id)) {
      if (filtered.length > 0) setPrimary(savePrimary(filtered[0]));
      else setPrimary(savePrimary(undefined));
    }
    setWishArray(saveWishArray(filtered));
    setWishChanged(isHolidayOrganization ? 'wish_removed' : 'course_removed')
  };

  return (
    askHolidayType ? <Redirect to={generatePath(welcome, {organization})}/> :
      <Page title={<FormattedMessage {...isHolidayOrganization ? messages.holiday_search : messages.course_search} />}>
        <WishConfirmModal
          holiday={isHolidayOrganization} onClose={() => setConfirm(false)}
          onConfirm={navigate} open={confirm} array={wishArray} isHolidayOrganization={isHolidayOrganization}/>
        <DataErrorFallback error={holidays.error}>
          <Instructions isMobile={isMobile}/>
          <Typography variant="h6" paragraph component="h2">
            <FormattedMessage {...isHolidayOrganization ? messages.selected_wish : messages.selected_course}
                              values={{v: wishArray.length}}/>
          </Typography>
          <AlertWithTimer isOpen={wishChanged} setOpen={setWishChanged}>
            {wishChanged && <FormattedMessage {...messages[wishChanged]} />}
          </AlertWithTimer>
          {wishArray && wishArray.length > 0 ?
            <HolidayTable isHolidayOrganization={isHolidayOrganization}
                          primary={primary} array={wishArray} wishes showPartner={!hidePartnerColumn}
                          isMobile={isMobile}
                          organization={organization} selectPrimary={selectPrimary} actionRemove={removeWish}/> :
            <Typography className={classes.none}>
              <FormattedMessage {...isHolidayOrganization ? messages.no_wish : messages.no_course} />
            </Typography>}
          {buttonProps.size < 1 ?
            <TooltipSearch {...buttonProps} isHolidayOrganization={isHolidayOrganization}/> :
            <ApplyButton {...buttonProps} />}
          <Filter
            showPartner={!hidePartnerColumn}
            filter={filter}
            isMobile={isMobile}
            setFilter={setFilter}/>
          <HolidayTable
            isHolidayOrganization={isHolidayOrganization}
            showPartner={!hidePartnerColumn}
            setFilter={setFilter}
            organization={organization}
            array={holidays?.results.filter((holiday) => !wishArray.find((wish) => wish.id === holiday.id))}
            props={{pagination}}
            isMobile={isMobile}/>
        </DataErrorFallback>
      </Page>
  );
};
